import React, { useContext, useEffect, useState } from 'react';
import { EventContext } from '../context/EventContext';
import { getEventByCode } from '../services/eventService';
import { Event } from '../types/Event';
import { CustomRegistrationQuestion, EventSelectedRegistrationQuestion } from '../types/RegistrationQuestion';
import { getEventSelectedRegistrationQuestions } from '../services/eventSelectedRegistrationQuestionService';
import { RegistrationQuestionComponent } from './RegistrationQuestionComponent';
import logo from '../assets/logo.png';
import { UserRoleContext } from '../context/UserRoleContext';
import { useAuth } from '../context/AuthContext';
import { Answer, CreateRegistrant, CustomQuestionAnswer } from '../types/Registrant';
import { createRegistrant } from '../services/registrantService';
import { uploadAndRegisterFile } from '../services/registrantFileService';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { Organization } from '../types/Organization';
import { getOrganizationByCode } from '../services/organizationService';
import { getCustomRegistrationQuestionsForEvent } from '../services/customRegistrationQuestionService';
import { RegistrationCustomQuestionComponent } from './RegistrationCustomQuestionComponent';
import { ROUTES } from '../constants/routes';
import { formatDate } from '../services/dateService';
import { formatPhoneNumber, prettyDateTime, prettyFormattedPhoneNumber } from '../services/formatter.service';
import { checkPhoneNumberStopped } from '../services/phoneNumberService';


export const CreateRegistrantComponent: React.FC = () => {
  const { currentEvent, setCurrentEvent } = useContext(EventContext); // Event context
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const { userRole } = useContext(UserRoleContext);
  const [eventCode, setEventCode] = useState<string>(''); // State for the event code
  const [organizationCode, setOrganizationCode] = useState<string>(''); // State for the event code
  const [searchedEvent, setSearchedEvent] = useState<Event | null>(null); // State for the returned event
  const [searchedOrganization, setSearchedOrganization] = useState<Organization | null>(null); // State for the returned event
  const [showEventSearchComponents, setShowEventSearchComponents] = useState<boolean>(!currentEvent); // Boolean for showing search components
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // State for error message
  const [eventSelectedQuestions, setEventSelectedQuestions] = useState<EventSelectedRegistrationQuestion[]>([]);
  const [customRegistrationQuestions, setCustomRegistrationQuestions] = useState<CustomRegistrationQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string | number | File>>({});
  const [customAnswers, setCustomAnswers] = useState<Record<string, string | number>>({});
  const [registrantIsUser, setRegistrantIsUser] = useState<boolean>(false);
  const [associateOrganization, setAssociateOrganization] = useState<boolean>(true);
  const [willCreateAccount, setWillCreateAccount] = useState<boolean>(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { event_code } = useParams<{ event_code: string }>();
  const [TandC, setTandC] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { addToast } = useToast();
  const [eventClosed, setEventClosed] = useState<boolean>(false);
  const [MandD, setMandD] = useState<boolean>(false);
  const [stoppedPhoneNumberString, setStoppedPhoneNumberString] = useState<string>("");




  const { isAuthenticated, user } = useAuth();
  const [regDetailAnswers, setRegDetailAnswers] = useState<Record<string, string | number>>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });



  const handleAnswer = (id: string, value: string | number | File) => {
    console.log(id, value);
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [id]: value,
    }));
  };

  const handleCustomAnswer = (id: string, value: string | number) => {
    setCustomAnswers((prevAnswers) => ({
      ...prevAnswers,
      [id]: value,
    }));
  };

  const setEventClosedWithErrorMessage = () => {
    setEventClosed(true);
    setErrorMessage('Registration for this event is currently closed.');
    addToast('Registration for this event is currently closed.');
  };

  const searchByEventCode = async (eventCode: string) => {
    try {
      const event = await getEventByCode(eventCode); // Simulate an API call to get event by code
      if (event) {
        setSearchedEvent(event);
        setErrorMessage(null);
        if (!event.event_open || !event.registration_open) {
          setEventClosedWithErrorMessage();
        }
        else {
          setCurrentEvent(event);
          setShowEventSearchComponents(false);
        }
      } else {
        setSearchedEvent(null);
        setErrorMessage('Event not found. Please check the code and try again.');
      }
    } catch (error) {
      setErrorMessage('An error occurred while searching for the event.');
      setSearchedEvent(null);
    }
  };

  const searchByOrganizationCode = async (organizationCode: string) => {
    try {
      const organization = await getOrganizationByCode(organizationCode); // Simulate an API call to get event by code
      if (organization) {
        setSearchedOrganization(organization);
        setErrorMessage(null); // Clear any previous error message
        setCurrentOrganization(organization);
      } else {
        setSearchedOrganization(null);
        setErrorMessage('Organization not found. Please check the code and try again.');
        addToast('Organization not found. Please check the code and try again.', true);

      }
    } catch (error) {
      setErrorMessage('An error occurred while searching for the organization.');
      setSearchedEvent(null);
      addToast('An error occurred while searching for the organization.', true);

    }
  };

  const handleSearchEvent = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    searchByEventCode(eventCode);
  };


  const handleSelectEvent = () => {
    if (searchedEvent) {
      setCurrentEvent(searchedEvent);
      setShowEventSearchComponents(false); // Hide search components after selecting
    }
  };

  const handleSelectOrganization = () => {
    if (searchedOrganization) {
      setCurrentOrganization(searchedOrganization);
    }
  };

  const handleRemoveOrganization = () => {
    if (searchedOrganization) {
      setCurrentOrganization(null);
    }
  };

  useEffect(() => {
    const loadFromEventCodeOnStartup = async () => {
      if (event_code) {
        try {
          const event = await getEventByCode(event_code); // Simulate an API call to get event by code
          if (event) {
            setSearchedEvent(event);
            setCurrentEvent(event);
            setShowEventSearchComponents(false);
            setErrorMessage(null); // Clear any previous error message
            if (!event.event_open || !event.registration_open) {
              setEventClosedWithErrorMessage();
            }
          } else {
            setShowEventSearchComponents(true);
            setSearchedEvent(null);
            addToast('Event not found. Please check the code and try again.', true);
          }
        } catch (error) {
          setErrorMessage('An error occurred while searching for the event.');
          setSearchedEvent(null);
        }
      }
    };
    loadFromEventCodeOnStartup();
  }, []);

  const handleEventSearchButtonPressed = () => {
    searchByEventCode(eventCode);
  };

  const handleOrganizationSearchButtonPressed = () => {
    if (organizationCode.trim() == '') {
      setSearchedOrganization(null);
      setCurrentOrganization(null);
    } else {
      searchByOrganizationCode(organizationCode.trim());
    }
  };

  // Handle the answer submission or change
  const handleRegDetailsAnswer = (id: string, value: string | number) => {
    setRegDetailAnswers((prevAnswers) => ({
      ...prevAnswers,
      [id]: value,
    }));
  };

const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
  let newValue = value;

  if (name === 'phoneNumber') {
    // Keep only digits
    const digits = newValue.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      newValue = digits;
    } else if (digits.length <= 6) {
      newValue = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      newValue = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  }

  setRegDetailAnswers((prev) => ({ ...prev, [name]: newValue }));
};


  const handlePhoneNumberBlur = async () => {
    const digits = (regDetailAnswers.phoneNumber as string).replace(/\D/g, '');
    const formatted = formatPhoneNumber('+1' + digits);
    if (!formatted) return;

    try {
      const result = await checkPhoneNumberStopped(formatted);
      if (result.valid && result.stopped && result.sender_phone_number != "" && result.sender_phone_number) {
        const pfphone = prettyFormattedPhoneNumber(formatPhoneNumber(result.sender_phone_number as string) as string);
        const message = `This phone number has opted out of SMS communication. To resume receiving texts from Cyne Up, reply with 'START' to ${pfphone} from this number.`
        setStoppedPhoneNumberString(message);
      } else {
        setStoppedPhoneNumberString("");
      }
    } catch (err) {
      console.error('Failed to check phone number:', err);
      setStoppedPhoneNumberString("");
    }
  };

  // Fetch events when the component mounts
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        if (currentEvent) {
          //update event
          const questions = await getEventSelectedRegistrationQuestions(currentEvent?.event_id);//(currentEvent?.event_id); // Fetch the events
          setEventSelectedQuestions(questions); // Update the state with fetched events
          const customQuestions = await getCustomRegistrationQuestionsForEvent(currentEvent?.event_id);
          setCustomRegistrationQuestions(customQuestions);
          if (!currentEvent.event_open || !currentEvent.registration_open) {
            setEventClosedWithErrorMessage();
          }
        }
      } catch (error) {
        console.error("Error fetching event:", error); // Log any errors
      }
    };
    fetchEvents();
  }, [currentEvent]); // Empty dependency array ensures this runs once on mount


  const submitRegistrant = async () => {
    setIsLoading(true);
    try {
      if (!currentEvent) {
        setErrorMessage("No event selected.");
        addToast("No event selected.", true);
        setIsLoading(false);
        return;
      }

      if (!TandC) {
        setErrorMessage("Please acknowledge the Terms of Service and Privacy Policy.");
        addToast('Please acknowledge the Terms of Service and Privacy Policy.', true);
        setIsLoading(false);
        return;
      }

      // if (currentEvent.require_organization && !(currentOrganization || (userRole?.organization_id))) {
      //   setErrorMessage("Organization required. Please enter an organization.");
      //   addToast("Organization required. Please enter an organization.", true);

      //   return;
      // }

      // Basic validation
      if (!regDetailAnswers.firstName || !regDetailAnswers.lastName || !regDetailAnswers.email) {
        setErrorMessage("Please fill in all required fields (First Name, Last Name, Email).");
        setIsLoading(false);
        return;
      }

      for (const question of eventSelectedQuestions) {
        if (
          question.is_required &&
          (answers[question.event_selected_registration_question_id] === '' ||
            answers[question.event_selected_registration_question_id] === undefined ||
            answers[question.event_selected_registration_question_id] === null)
        ) {
          addToast(`Missing question '${question.question_text}'`);
          setErrorMessage('Missing required question fields');
          setIsLoading(false);
          return;
        }
      }

      // Check for missing required custom question answers
      for (const customQuestion of customRegistrationQuestions) {
        if (
          customQuestion.is_required &&
          (customAnswers[customQuestion.custom_registration_question_id] === '' ||
            customAnswers[customQuestion.custom_registration_question_id] === undefined ||
            customAnswers[customQuestion.custom_registration_question_id] === null)
        ) {
          addToast(`Missing question '${customQuestion.custom_question_text}'`);
          setErrorMessage('Missing required question fields');
          setIsLoading(false);
          return;
        }
      }



      if (willCreateAccount) {
        if (!password || !confirmPassword) {
          setErrorMessage("Please enter and confirm your password.");
          setIsLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          setErrorMessage("Passwords do not match.");
          setIsLoading(false);
          return;
        }
        if (password.length < 8) {
          setErrorMessage("Password must be at least 8 characters long.");
          setIsLoading(false);
          return;
        }
      }
      // Build the registration answers array without the files;
      const fileQuestionIds = new Set(
        eventSelectedQuestions
          .filter(q => q.question_type === 'file')
          .map(q => String(q.event_selected_registration_question_id))
      );


      const registrationAnswers: Answer[] = Object.entries(answers)
        .filter(([question_id]) => !fileQuestionIds.has(question_id))
        .map(([question_id, answer_value]) => ({
          preset_registration_question_id: question_id,
          answer: String(answer_value),
        }));

      const fileUploads = Object.entries(answers)
        .filter(([question_id, value]) =>
          fileQuestionIds.has(question_id) && value instanceof File
        )
        .map(([question_id, file]) => ({
          preset_registration_question_id: question_id,
          file: file as File,
        }));

      const answersArray: CustomQuestionAnswer[] = Object.entries(customAnswers).map(
        ([custom_registration_question_id, answer_text]) => ({ question_text: '', custom_registration_question_id, answer_text })
      );
      const digits = (regDetailAnswers.phoneNumber as string).replace(/\D/g, '');
      const formattedPhoneNumber = formatPhoneNumber(String('+1' + digits));
      if (!formattedPhoneNumber) {
        addToast("Phone number is not in proper format. Please make sure it is a 10 digit phone number.", true);
        setIsLoading(false);
        return;
      }

      const registrant: CreateRegistrant = {
        event_id: currentEvent.event_id,
        first_name: String(regDetailAnswers.firstName),
        last_name: String(regDetailAnswers.lastName),
        email: String(regDetailAnswers.email),
        phone_number: formattedPhoneNumber, // optional
        answers: registrationAnswers,
        custom_answers: answersArray,
        create_account: willCreateAccount,
        registrant_is_user: registrantIsUser, // you might need to include this if your API expects it
        ...(associateOrganization ? { organization_id: userRole?.organization_id } : {}),
        ...(searchedOrganization ? { organization_id: searchedOrganization.organization_id } : {}),

      };

      const registrant_id = await createRegistrant(registrant);
      if (registrant_id) {
        if (registrant_id == '1') {
          addToast(`${registrant.first_name} has already signed up for this event with this email and phone number. Please verify the information and contact the event host if the issue persists.`, true);
          setIsLoading(false);
          return;
        }

        for (const { preset_registration_question_id, file } of fileUploads) {
          await uploadAndRegisterFile(registrant_id, file, preset_registration_question_id);
        }
        addToast(`${registrant.first_name} has been successfully Cyned Up for ${currentEvent.event_name}! Thank you for using the Fastest Cyber Sign Up! You may now close this tab.`)
        navigate(ROUTES.REGISTRATION_SUCCESS);


      } else {
        addToast(`Server error registering, please try again in a few minutes.`, true);
        setErrorMessage("Failed to register. Please try again.");
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      addToast('An unexpected error occurred during registration.', true);
      setErrorMessage("An unexpected error occurred during registration.");
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  async function pathToFile(path: string, filename: string, mimeType = 'application/octet-stream'): Promise<File> {
    const response = await fetch(path);
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  }

  return (
    <div style={{ alignContent: 'center', justifyContent: 'center', display: 'flex', width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logo} alt="Cyne Up" style={{ maxWidth: '40%', minWidth: '200px' }} />
        </div>
        <div style={{ display: 'flex', marginRight: '5px', alignItems: 'center' }}>
          <h2 style={{ marginRight: '20px' }}>
            {currentEvent ? `${currentEvent?.event_name} - (${currentEvent?.event_code})` : 'Select an Event'}
          </h2>
          {(currentEvent && !showEventSearchComponents && !event_code) &&
            <button onClick={() => setShowEventSearchComponents(true)}>
              Switch Event
            </button>}
        </div>
        {showEventSearchComponents && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                id="eventCode"
                type="text"
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value)}
                placeholder="Enter event code"
                style={{ padding: '8px', flexGrow: 1, marginRight: '10px', textTransform: 'uppercase' }}
              />
              <button style={{ marginTop: '-10px' }}
                type="submit"
                onClick={handleEventSearchButtonPressed}
              >
                Search
              </button>
            </div>
            {errorMessage && (
              <p style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</p>
            )}
            {/* {searchedEvent && (
              <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #ddd' }}>
                <p>
                  <strong>Event Name:</strong> {searchedEvent.event_name}
                </p>
                <p>
                  <strong>Date:</strong> {formatDate(searchedEvent.start_date)}
                </p>
                <button
                  type="button"
                  onClick={handleSelectEvent}
                  style={{
                    padding: '10px 20px',
                    background: 'green',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Select
                </button>
              </div>
            )} */}
          </>
        )}

        {/* Organization Search */}
        {/* {(!eventClosed && currentEvent?.require_organization == true && (userRole?.organization_id == "" || userRole?.organization_id == null)) && (
          <div>
            <label><strong>Organization Code</strong>{currentEvent?.require_organization && <span style={{ color: 'red' }}> *</span>}</label>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                id="organizationCode"
                type="text"
                value={organizationCode}
                onChange={(e) => setOrganizationCode(e.target.value)}
                onBlur={handleOrganizationSearchButtonPressed}
                placeholder="Example: 1B09SF"
                style={{ padding: '8px', flexGrow: 1, marginRight: '10px', textTransform: 'uppercase' }}
              />
              {/* <button style={{ marginTop: '-10px' }}
                type="submit"
                onClick={handleOrganizationSearchButtonPressed}
              >
                Search
              </button> */}
        {/*</div>
            {errorMessage && (
              <p style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</p>
            )}
            {searchedOrganization && (
              <div style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd' }}>
                <p>
                  <strong>Organization Name:</strong> {searchedOrganization.organization_name}
                </p>
                <p>
                  <strong>Location</strong> {searchedOrganization.city}, {searchedOrganization.state}
                </p>
                {!currentOrganization && <button
                  type="button"
                  onClick={handleSelectOrganization}
                  style={{
                    padding: '10px 20px',
                    background: 'green',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Select
                </button>}
                {/* {currentOrganization && <button
                  type="button"
                  onClick={handleRemoveOrganization}
                  style={{
                    padding: '10px 20px',
                    background: 'green',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Remove Organization
                </button>} */}
        {/*</form></div>
            )}
          </div>
        )} */}

        {/* Rest of form */}
        {(currentEvent && !eventClosed) && <div>

          {isAuthenticated && (<div className="form-group" style={{ marginTop: '10px' }}>
            <label htmlFor="registrant_is_user"><strong>Are you registering for yourself?</strong><span style={{ color: 'red' }}> *</span></label>
            <div style={{ display: "flex", gap: "30px" }}>
              <label>
                <input style={{ width: '40px' }}
                  type="radio"
                  id="registrant_is_user"
                  name="registrant_is_user"
                  value="1"
                  checked={registrantIsUser}
                  onChange={() => setRegistrantIsUser(true)}
                />
                Yes, myself.
              </label>
              <label>
                <input style={{ width: '40px' }}
                  type="radio"
                  id="registrant_is_user"
                  name="registrant_is_user"
                  value="0"
                  checked={!registrantIsUser}
                  onChange={() => setRegistrantIsUser(false)}
                />
                No, someone else.
              </label>
            </div>
          </div>)}

          {/* {!isAuthenticated && (
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={willCreateAccount}
                  onChange={(e) => setWillCreateAccount(e.target.checked)}
                />
                Create a Cyne Up Account
              </label>

              {willCreateAccount && (
                <div style={{ marginTop: "1rem" }}>
                  <div>
                    <label>Password</label>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginTop: "0.5rem" }}>
                    <label>Confirm Password</label>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )} */}

          {userRole?.organization_id && (<div className="form-group">
            <label htmlFor="registrant_is_user"><strong>Do you want to associate this registrant with {userRole.organization_name}?</strong><span style={{ color: 'red' }}> *</span></label>
            <div style={{ display: "flex", gap: "30px" }}>
              <label>
                <input style={{ width: '40px' }}
                  type="radio"
                  id="associate_organization"
                  name="associate_organization"
                  value="1"
                  checked={associateOrganization}
                  onChange={() => setAssociateOrganization(true)}
                />
                Yes
              </label>
              <label>
                <input style={{ width: '40px' }}
                  type="radio"
                  id="associate_organization"
                  name="associate_organization"
                  value="0"
                  checked={!associateOrganization}
                  onChange={() => setAssociateOrganization(false)}
                />
                No
              </label>
            </div>
          </div>)}

          {/* Input for First Name */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>First Name<span style={{ color: 'red' }}> *</span></strong>
              <input
                type="text"
                name="firstName"
                value={regDetailAnswers.firstName}
                // value={typeof answers.firstName === 'string' ? answers.firstName : ''}
                onChange={handleTextInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          {/* Input for Last Name */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>Last Name<span style={{ color: 'red' }}> *</span></strong>
              <input
                type="text"
                name="lastName"
                value={regDetailAnswers.lastName}
                // value={typeof answers.lastName === 'string' ? answers.lastName : ''}
                onChange={handleTextInputChange}
                required
                minLength={1}
                maxLength={100}
              />
            </label>
          </div>

          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>Mobile Phone Number <span style={{ color: 'gray' }}> (Used for event messaging and alerts)</span><span style={{ color: 'red' }}> *</span></strong>
              <div style={{ display: 'flex', gap: '5px' }}>
                <input
                  style={{ maxWidth: '24px'}}
                  type="text"
                  value={'+1'}
                  minLength={0}
                  maxLength={0}
                  placeholder='+1'
                  readOnly={true}
                  disabled={true}
                />
                <input
                  type="text"
                  name="phoneNumber"
                  value={regDetailAnswers.phoneNumber}
                  onChange={handleTextInputChange}
                  onBlur={handlePhoneNumberBlur}
                  required
                  minLength={14}
                  maxLength={14}
                  placeholder='(555) 555-555'
                />
              </div>
            </label>
            {(stoppedPhoneNumberString != "") && <label>{stoppedPhoneNumberString}</label>}
          </div>

          {/* Input for Email */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '500px' }}>
            <label>
              <strong>Email <span style={{ color: 'gray' }}>(Used for event messaging and alerts)</span><span style={{ color: 'red' }}> *</span></strong>
              <input
                type="email"
                name="email"
                value={regDetailAnswers.email}
                // value={typeof answers.phoneNumber === 'string' ? answers.phoneNumber : ''}
                onChange={handleTextInputChange}
                required
                minLength={5}
                maxLength={255}
              />
            </label>
          </div>

          {/* {!showEventSearchComponents && currentEvent && !event_code && (
            <>
              <p style={{ marginTop: '20px', fontStyle: 'italic' }}>
                You are currently registering for this event.{' '}
                <span
                  onClick={() => setShowEventSearchComponents(true)}
                  style={{
                    color: 'blue',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Switch Event
                </span>
              </p>
            </>
          )} */}

          {currentEvent && eventSelectedQuestions.length > 0 && eventSelectedQuestions.map((question) => (
            <RegistrationQuestionComponent
              key={question.event_selected_registration_question_id}
              question={question}
              onAnswer={handleAnswer}
              answer={answers[question.event_selected_registration_question_id]}
              event_id={currentEvent.event_id}
            // Provide the pre-filled answer if any
            />
          ))}

          {currentEvent && customRegistrationQuestions.length > 0 && customRegistrationQuestions.map((question) => (
            <RegistrationCustomQuestionComponent
              key={question.custom_registration_question_id}
              question={question}
              onCustomAnswer={handleCustomAnswer}
              customAnswer={customAnswers[question.custom_registration_question_id]}
              event_id={currentEvent.event_id}
            // Provide the pre-filled answer if any
            />
          ))}

          <p><strong>Cyne Up values your privacy. Cyne Up does not sell, rent, or share your personal information with 3rd parties.</strong></p>

          <div className="form-group">
            <div style={{ display: 'flex', flexDirection: 'row', gap: '5px', justifyContent: 'space-between' }}>
              <input
                type="checkbox"
                id="terms_and_conditions"
                name="terms_and_conditions"
                checked={TandC}
                onChange={() => setTandC(!TandC)}
                style={{ marginBottom: '0px', marginRight: '5px' }}

              />
              <label htmlFor="terms_and_conditions" style={{ textAlign: 'left' }}>

                <strong>I acknowledge that I have read and agree to be bound by the <a target="_blank" href="https://cyneup.com/terms-of-service">Terms of Service</a> and <a target="_blank" href="https://cyneup.com/privacy-policy">Privacy Policy</a>{registrantIsUser ? `.` : `, and I confirm that I have the authority or permission to submit this information on behalf of the registrant.`}</strong>
              </label>
            </div>
          </div>
          <br />
          <div className="form-group">
            <div style={{ display: 'flex', flexDirection: 'row', gap: '5px', justifyContent: 'space-between' }}>
              <input
                type="checkbox"
                id="messaging_and_data"
                name="messaging_and_data"
                checked={MandD}
                onChange={() => setMandD(!MandD)}
                style={{ marginBottom: '0px', marginRight: '5px' }}
              />
              <label htmlFor="terms_and_conditions" style={{ textAlign: 'left' }}>
                <strong>
                  {/* { registrantIsUser ? */}
                  {`By opting in, I agree to receive communications from Cyne Up regarding ${currentEvent.event_name} via email and SMS using the contact information provided. I confirm that I have the permission or legal authority to provide this contact information on behalf of the registrant. Message frequency may vary and may include multiple messages per hour before, during, and after the event. Standard message and data rates may apply. I understand that I may opt out at any time by replying STOP to any SMS or by using the unsubscribe link in any email.`}
                  {/* : `I agree to receive event-related communications from Cyne Up via email and SMS at the contact information provided, and I confirm that I have the permission or legal authority to provide this contact information on behalf of the registrant. Standard message and data rates may apply. I understand I may opt out at any time.` } */}
                </strong>
              </label>
            </div>
          </div>
          <br />
          <div style={{ display: 'flex', position: 'relative', justifyContent: 'center' }}>
            {!!isLoading && <div className='spinner'></div>}
            <button style={{ width: '200px', height: '50px', fontSize: '24px', position: 'relative' }} disabled={(!currentEvent && !searchedEvent) || !TandC || !MandD || isLoading || currentEvent.is_locked} onClick={() => submitRegistrant()}>{isLoading ? `` : `Cyne Up`}</button>
          </div>
          {/* <button
            disabled={!currentEvent}
            onClick={() => submitRegistrant()}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              width: '200px',
            }}
          >
            <img
              src={logo}
              alt="Cyne Up"
              style={{
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </button> */}
        </div>}
        {eventClosed && <div><h2>Registration for this event is currently closed.</h2>
          {/* <button type="button" onClick={() => { navigate(ROUTES.HOME); }}>Go To Home Screen</button> */}
        </div>}
      </form>
    </div>
  );
};
